import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import AcceptanceConfirmationModal from '../../../sharedModules/layout/AcceptanceConfirmationModal';
import DataTable from '../../../sharedModules/dataTable';
import DeleteIconButton from '../../../sharedModules/layout/DeleteIconButton';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import SubmitCachedDataModal from '../components/SubmitCachedDataModal';

import { BackflowJobGroup, BackflowJobItem } from '../../model';
import { loadJobGroupFromCache } from '../../utils/backflowCache';

interface CachedJobGroup {
  jobGroup: BackflowJobGroup;
  jobItems: BackflowJobItem[];
  cachedAt: string;
  unsyncedCount: number;
}

const UnsubmittedTestsPage: React.FunctionComponent = () => {
  const [cachedJobGroups, setCachedJobGroups] = useState<CachedJobGroup[]>([]);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedJobGroup, setSelectedJobGroup] = useState<any>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState<boolean>(false);
  const [jobGroupIdToClear, setJobGroupIdToClear] = useState<string | null>(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadCachedJobGroups = () => {
    try {
      const jobGroupKeys = Object.keys(localStorage).filter((key) => key.startsWith('backflowJobGroup_'));

      const cached: CachedJobGroup[] = jobGroupKeys
        .map((key) => {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;

            const parsed = JSON.parse(raw);
            const jobGroupId = key.replace('backflowJobGroup_', '');
            const result = loadJobGroupFromCache(jobGroupId);
            if (!result) return null;

            const unsyncedCount =
              result.jobItems?.filter((item: any) => item.needsSync || item.backflow_log?.needsSync).length || 0;

            return {
              jobGroup: result.jobGroup,
              jobItems: result.jobItems,
              cachedAt: parsed.cachedAt,
              unsyncedCount,
            };
          } catch (error) {
            console.error(`Failed to parse localStorage key ${key}:`, error);
            return null;
          }
        })
        .filter((item): item is CachedJobGroup => item !== null)
        .sort((a, b) => new Date(b.cachedAt).getTime() - new Date(a.cachedAt).getTime());

      setCachedJobGroups(cached);
    } catch (error) {
      console.error('Failed to load cached job groups:', error);
    }
  };

  useEffect(() => {
    loadCachedJobGroups();
  }, []);

  const handleClearCache = (jobGroupId: string) => {
    try {
      const cacheKey = `backflowJobGroup_${jobGroupId}`;
      localStorage.removeItem(cacheKey);
      loadCachedJobGroups();
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  };

  const handleClearCacheConfirmed = () => {
    if (jobGroupIdToClear) handleClearCache(jobGroupIdToClear);
    setJobGroupIdToClear(null);
  };

  const handleRequestClearCache = (jobGroupId: string) => {
    setJobGroupIdToClear(jobGroupId);
    setClearConfirmOpen(true);
  };

  const handleOpenSubmitModal = (row: CachedJobGroup) => {
    const result = loadJobGroupFromCache(row.jobGroup.id?.toString());
    if (result) {
      setSelectedJobGroup(result);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedJobGroup(null);
  };

  const handleSubmitSuccess = () => {
    loadCachedJobGroups(); // Refresh the list after successful submission
  };

  const columns = [
    {
      name: 'Order',
      selector: (row: CachedJobGroup) => row.jobGroup.order_no,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Customer',
      selector: (row: CachedJobGroup) => row.jobGroup.company_name,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Address',
      selector: (row: CachedJobGroup) => row.jobGroup.address1,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Devices',
      selector: (row: CachedJobGroup) => row.jobItems?.length || 0,
      sortable: true,
      width: '100px',
    },
    {
      name: 'Cached At',
      selector: (row: CachedJobGroup) => row.cachedAt,
      sortable: true,
      width: '180px',
      cell: (row: CachedJobGroup) => format(new Date(row.cachedAt), 'MMM dd, yyyy h:mm a'),
    },
    {
      name: 'Actions',
      width: '220px',
      cell: (row: CachedJobGroup) => (
        <Grid container spacing={1}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenSubmitModal(row);
              }}
            >
              Review
            </Button>
          </Grid>
          <Grid item>
            <DeleteIconButton onClick={() => handleRequestClearCache(row.jobGroup.id?.toString())} />
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Unsubmitted Backflow Work Orders" titleIcon={<PlumbingIcon />} />
      </Grid>

      {!isOnline && (
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Alert severity="warning">
            You are currently offline. Any unsubmitted logs shown below are saved on your device and can be submitted
            when you're connected.
          </Alert>
        </Grid>
      )}

      {cachedJobGroups.length === 0 && (
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Alert severity="info">No unsubmitted test logs found. You're all caught up!</Alert>
        </Grid>
      )}

      {cachedJobGroups.length > 0 && (
        <>
          <Grid item xs={12} style={{ marginTop: 20, marginBottom: 10 }}>
            <Typography variant="body2" color="textSecondary">
              This page shows all backflow work orders saved on your device from when you were working offline.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <DataTable
                  title=""
                  columns={columns}
                  data={cachedJobGroups}
                  dense
                  highlightOnHover
                  pointerOnHover
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[25, 50, 100]}
                  noHeader
                />
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      <AcceptanceConfirmationModal
        title="Clear Cached Data"
        message="Are you sure you want to clear this work order from your device? Any unsubmitted data will be lost."
        modalOpen={clearConfirmOpen}
        setModalOpen={setClearConfirmOpen}
        onConfirm={handleClearCacheConfirmed}
      />

      {/* Submit Modal */}
      {selectedJobGroup && (
        <SubmitCachedDataModal
          open={modalOpen}
          onClose={handleModalClose}
          jobGroupId={selectedJobGroup.jobGroup.id}
          jobGroupData={selectedJobGroup}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}
    </Grid>
  );
};

export default UnsubmittedTestsPage;
